import { useCallback, useMemo, useState } from 'react';
import { get } from 'lodash';

export type FilterValue = string | number | null | undefined;

export interface ColumnFilters<T> {
  [field: string]: FilterValue;
}

export function useFilter<T>(rows: T[]) {
  const [filters, setFilters] = useState<ColumnFilters<T>>({});

  const onFilterChange = useCallback((field: string, value: FilterValue) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === null || value === undefined || value === '') {
        delete newFilters[field];
      } else {
        newFilters[field] = value;
      }
      return newFilters;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const filteredRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    if (!filters || Object.keys(filters).length === 0) return rows;

    return rows.filter((row) => {
      return Object.entries(filters).every(([field, filterValue]) => {
        if (filterValue === null || filterValue === undefined || filterValue === '') {
          return true;
        }

        const cellValue = get(row, field);
        return cellValue === filterValue; // Exact match filtering
      });
    });
  }, [filters, rows]);

  return useMemo(
    () => ({
      filteredRows,
      filters,
      onFilterChange,
      clearFilters,
    }),
    [filteredRows, filters, onFilterChange, clearFilters],
  );
}
